import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Alert, CircularProgress, Tooltip } from '@mui/material';
import type { Schema } from '../../services/api';
import type { SchemaDeleteResponse } from '../../services/schemaApi';

interface DeleteSchemaSetDialogProps {
  open: boolean;
  schema: Schema | null | undefined;
  /** @deprecated Use schema instead */
  schemaSet?: Schema | null | undefined;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  deleteError?: string;
  dependencyInfo?: {
    hasDependencies: boolean;
    dependencies: Record<string, any>;
    totalCount: number;
  };
}

const DeleteSchemaSetDialog: React.FC<DeleteSchemaSetDialogProps> = ({
  open,
  schema,
  schemaSet,
  onClose,
  onConfirm,
  isLoading,
  deleteError,
  dependencyInfo,
}) => {
  const targetSchema = schema ?? schemaSet;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Delete Schema</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete schema{' '}
          <strong>{targetSchema?.schema_name ?? 'this schema'}</strong>? This action cannot be undone.
        </Typography>

        {dependencyInfo?.hasDependencies && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Cannot delete:</strong> This schema has {dependencyInfo.totalCount} dependency{dependencyInfo.totalCount > 1 ? 'ies' : ''}.
              Please remove dependencies first.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Dependencies:</strong>{' '}
              {Object.entries(dependencyInfo.dependencies).map(([type, count]) => {
                if (type === 'projects' && typeof count === 'object') {
                  return `${count.count} project${count.count > 1 ? 's' : ''}`;
                }
                return `${count} ${type}${count > 1 ? 's' : ''}`;
              }).join(', ')}
            </Typography>
          </Alert>
        )}

        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">{deleteError}</Typography>
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2">Checking dependencies and deleting...</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Tooltip
          title={
            isLoading
              ? "Checking dependencies and deleting schema..."
              : dependencyInfo?.hasDependencies
              ? `Cannot delete: This schema has ${dependencyInfo.totalCount} dependency${dependencyInfo.totalCount > 1 ? 'ies' : ''}. Please remove dependencies first.`
              : "Delete this schema permanently"
          }
          placement="top"
        >
          <Button
            color="error"
            variant="contained"
            onClick={onConfirm}
            disabled={isLoading || dependencyInfo?.hasDependencies}
            sx={{ minWidth: 100 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteSchemaSetDialog;
